<template>
    <section class="flex flex-col gap-3">
        <h2 class="body-small-semibold text-ink">Question by Question</h2>
        <div class="flex flex-col gap-3">
            <BaseCard v-for="(question, index) in questions" :key="question.question_id" padding="lg" class="flex flex-col gap-3">
                <div class="self-q-head flex flex-col gap-2 px-3 py-2.5 rounded-md">
                    <span class="body-small text-ink-muted">Question {{ ordinalWord(index + 1) }}</span>
                    <span class="body-small-bold text-ink">{{ resolveQuestionName(question.text || question.title, subjectFirstName ?? "") }}</span>
                </div>

                <div v-if="question.is_text && question.open_text_synthesis" class="flex flex-col gap-2">
                    <span class="body-small-semibold text-ink-muted px-2">Response Insights</span>
                    <ul class="flex flex-col gap-2 pl-6">
                        <li v-for="(line, i) in insightLines(question.open_text_synthesis)" :key="i" class="body-small text-ink list-disc">
                            {{ line }}
                        </li>
                    </ul>
                </div>

                <template v-else-if="question.is_score">
                    <div class="self-hist" :style="{ '--rows': maxScore }">
                        <div v-for="score in scoreBuckets" :key="score" class="self-hist__col">
                            <div class="self-hist__track">
                                <span class="self-hist__bar" :style="{ height: `${barHeight(question, score)}%` }"></span>
                            </div>
                            <span class="details text-ink-muted pt-2">{{ score }} · {{ countAt(question, score) }}</span>
                        </div>
                    </div>
                    <div v-if="question.self_score !== null" class="details text-ink-muted">Your rating {{ question.self_score }}/{{ maxScore }}</div>
                </template>
            </BaseCard>
        </div>
    </section>
</template>

<script setup lang="ts">
import type { PerQuestion } from "~vue/api/generated/reflections-admin";
import BaseCard from "~vue/components/BaseCard.vue";
import { ordinalWord, resolveQuestionName } from "~vue/utils/assessmentValidation";
import { computed } from "vue";

interface Props {
    questions: PerQuestion[];
    maxScore: number;
    // For self reflections this is the viewer's own first name. Question text
    // in the DB uses `{{ name }}` because the same templates also power 360s.
    subjectFirstName?: string;
}

const props = defineProps<Props>();

const scoreBuckets = computed(() => Array.from({ length: props.maxScore }, (_v, i) => i + 1));

function countAt(question: PerQuestion, score: number): number {
    return question.distribution?.[score - 1] ?? 0;
}

function barHeight(question: PerQuestion, score: number): number {
    // Since self-only cycles have a single response, the bar is either 0 or
    // full. We render at 90% (not 100%) to match the mockup, which leaves a
    // small gap above the top of the track.
    return countAt(question, score) > 0 ? 90 : 0;
}

// Split the LLM synthesis into visual bullets. Backend produces newline- or
// bullet-separated strings; keep it forgiving and drop empty lines.
function insightLines(synthesis: string): string[] {
    return synthesis
        .split(/\n|(?:^|\s)[•·]\s+/)
        .map((line) => line.replace(/^[-*]\s+/, "").trim())
        .filter((line) => line.length > 0);
}
</script>

<style scoped>
.self-q-head {
    background: var(--color-surface-sunken);
}

.self-hist {
    display: flex;
    align-items: flex-end;
    gap: 0;
    height: 120px;
    padding: 0 10px;
    border-bottom: 1px solid var(--color-border);
}

.self-hist__col {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    height: 100%;
}

.self-hist__track {
    flex: 1;
    width: 100%;
    display: flex;
    align-items: flex-end;
    justify-content: center;
    padding: 0 8px;
}

.self-hist__bar {
    width: 100%;
    max-width: 92px;
    /* Cyan is the Journeys family. This histogram only ever renders inside a
       self reflection, so it takes that flow's amber, which the ledger shares
       with pending status. */
    background: var(--color-status-warning);
    border-radius: 5px 5px 0 0;
    transition: height 320ms ease;
}

@media (prefers-reduced-motion: reduce) {
    .self-hist__bar {
        transition: none;
    }
}
</style>
